// CatalogStore — IndexedDB local pra snapshot do catálogo do parceiro.
// Permite navegação 100% local (sem spinner) entre Movies/Series/Live.
//
// Schema versionado: bump SCHEMA_VERSION quando o shape do snapshot mudar
// — abrir nova versão do DB limpa cache antigo automaticamente.
//
// Inclui última bandwidth medida (fundida com download do snapshot).
//
// ===== DB v2 — SPLIT POR SURFACE (perf) =====
// v1 guardava o snapshot INTEIRO (liveChannels + vodItems + series + ...) num
// único record, keyPath `providerId`, na store 'snapshots'. Ler QUALQUER coisa
// custava desserializar o record inteiro (5-15MB) — o structured clone do IDB
// materializa TUDO antes de devolver. Quem só queria o `storedAt` (providers.tsx
// no boot, useCatalogRefresh) pagava o snapshot completo por um inteiro.
//
// v2 splita em uma store POR SURFACE + uma store `meta` minúscula:
//   - getMeta(key)          → só contentHash/storedAt/bandwidth (barato, boot)
//   - getSurface(key, s)    → só a surface pedida (ex: liveChannels)
//   - get(key)              → record inteiro (COMPAT: quem precisa de tudo)
//
// `get()` continua existindo e com o MESMO shape de v1 de propósito: os
// consumidores que realmente precisam do snapshot inteiro (catalog-snapshot,
// useSearchSuggestions) seguem funcionando sem mudança.
//
// ⚠️ Migração v1→v2: não há migração de DADOS. A store antiga é destruída e o
// snapshot é re-baixado na próxima sessão (é cache, TTL 24h, custo = 1 fetch).
// Migrar in-place exigiria ler o record v1 inteiro dentro do `upgrade` — caro,
// arriscado (quota) e sem ganho: o dado se regenera sozinho.
//
// ===== DB v3 — ESCRITA FATIADA + PARCIAL LEGÍVEL (cota) =====
// v2 splitou a LEITURA por surface, mas a ESCRITA seguia numa transação
// multi-store única com as 6 surfaces. O IDB efetiva isso como unidade atômica:
// os ~10MB+ de uma conta grande eram cobrados de uma vez, a transação abortava
// com QuotaExceededError e NADA era gravado — inclusive os ~391KB de
// `liveChannels`, que cabem com folga. Na LG do dono (15.329 títulos) o
// IndexedDB ficou VAZIO em 3 boots e a camada de cache inteira era inerte.
// Medido no device, no mesmo padrão de transação: 5MB grava, 10MB estoura.
//
// v3 escreve uma transação POR SURFACE, na ordem de SURFACE_WRITE_PRIORITY, e
// `meta` ganha `surfaces` (quais entraram). Assim a cota é cobrada por fatia,
// `liveChannels` vai primeiro e sobrevive sozinho, e uma gravação parcial é
// LEGÍVEL em vez de ser descartada por inteiro.
//
// ⚠️ Migração v2→v3: metas v2 não têm `surfaces`. A base é zerada (mesma
// lógica do v1→v2: é cache, custa 1 fetch).
//
// ⚠️ Piso Chr38: a lib `idb` usa Proxy, que não existe no Chromium 38 — lá o
// openDB REJEITA e todo método aqui cai no catch e devolve null. Isso é o
// comportamento correto e desejado: o app degrada pro caminho de rede, limpo
// e silencioso. NADA aqui deve ser descrito como "funciona no piso".

import { getCapability } from '@app-iptv/device'
import type {
  EpgProgram, // mantido pra compat futura
  LiveCategory,
  LiveChannel,
  Series,
  VodItem,
} from '@app-iptv/iptv-client'
import type { DBSchema, IDBPDatabase } from 'idb'

import { makeDbOpener } from './idb-opener'
import { track } from './qoe'


// Bump aqui quando shape do snapshot mudar — nova DB version limpa cache antigo
export const SNAPSHOT_SCHEMA_VERSION = 1

export interface CatalogSnapshot {
  schemaVersion: number
  generatedAt: number
  providerId: string
  liveCategories: LiveCategory[]
  liveChannels: LiveChannel[]
  vodCategories: LiveCategory[]
  vodItems: VodItem[]
  seriesCategories: LiveCategory[]
  series: Series[]
  // Padding (opcional, vem do backend pra medição de banda) — descartar
  _padding?: string
}

interface SnapshotRecord {
  providerId: string // chave
  contentHash: string // do backend, usado em If-None-Match
  storedAt: number // unix sec — quando foi gravado localmente
  bandwidthMbps: number | null // medido durante download
  snapshot: CatalogSnapshot
}

/** Metadados do snapshot, SEM o payload. É o que basta pra decidir
 *  staleness/revalidação — leitura barata (boot). */
export interface SnapshotMeta {
  providerId: string
  contentHash: string
  storedAt: number
  bandwidthMbps: number | null
  schemaVersion: number
  generatedAt: number
  /** Surfaces REALMENTE gravadas nesta rodada. É o que torna a escrita parcial
   *  legível: sem esta lista, `meta` significaria "tudo entrou" e a única forma
   *  segura de tratar uma gravação incompleta seria descartar o conjunto todo —
   *  jogando fora `liveChannels` (o caminho crítico do hero) só porque
   *  `vodItems` não coube. Com ela, cada leitura sabe se a sua surface é
   *  confiável. */
  surfaces: SurfaceName[]
  /**
   * Hash de CONTEÚDO (calculado no cliente, lib/hash-conteudo.ts) de cada surface
   * gravada. É o que o disjuntor em `priority-only` compara pra saber se a
   * prioridade mudou DE VERDADE — o `generatedAt` e o ETag mudam a cada
   * reconstrução do snapshot no servidor (até ~48/dia), mesmo com ela idêntica.
   * Opcional: meta anterior a este campo não tem, e aí conta como "mudou" (uma
   * regravação, uma vez).
   */
  hashes?: Partial<Record<SurfaceName, string>>
  /**
   * Quando as surfaces foram GRAVADAS (unix sec). Diferente de `storedAt`, que o
   * `touch()` renova sem gravar nada: é este que mede o intervalo mínimo entre
   * regravações da prioridade.
   */
  writtenAt?: number
}

/** Surfaces persistidas, uma store por surface. As chaves batem 1:1 com os
 *  campos de array do CatalogSnapshot — o mapeamento é mecânico de propósito
 *  (ver toSurfaces/fromSurfaces): menos espaço pra alguém esquecer uma. */
export const SURFACE_NAMES = [
  'liveChannels',
  'liveCategories',
  'vodItems',
  'vodCategories',
  'series',
  'seriesCategories',
] as const

export type SurfaceName = (typeof SURFACE_NAMES)[number]

/** Ordem de gravação, do mais crítico pro mais sacrificável.
 *
 *  Isto NÃO é estética: a escrita é fatiada em uma transação por surface e a
 *  cota pode acabar no meio. Quem vem primeiro é quem sobrevive.
 *   - `liveChannels` (~391KB / ~1.040 canais na conta medida) é o caminho
 *     crítico do LiveHero: o 1º frame do vídeo depende dele. Perder isto é
 *     perder o motivo de existir do cache.
 *   - as três `*Categories` são de KB e servem de índice pras telas.
 *   - `series` e `vodItems` são o grosso (VOD sozinho é a maior parte dos
 *     15.329 títulos da conta medida) e degradam pra rede sem quebrar nada:
 *     a tela de Filmes volta a buscar como fazia antes do cache existir.
 *
 *  A ordem inversa derrubaria justo o caminho do vídeo pra guardar catálogo
 *  de VOD. Manter `liveChannels` em primeiro é a decisão de produto aqui. */
export const SURFACE_WRITE_PRIORITY: readonly SurfaceName[] = [
  'liveChannels',
  'liveCategories',
  'vodCategories',
  'seriesCategories',
  'series',
  'vodItems',
]

/** Surfaces sacrificáveis sob pressão de cota, da mais sacrificável pra menos.
 *  `liveChannels` e as categorias NUNCA entram aqui. */
export const SURFACE_SACRIFICIAL: readonly SurfaceName[] = ['vodItems', 'series']

/** Tipo do payload de cada surface — espelha CatalogSnapshot. */
export interface SurfaceValueMap {
  liveChannels: LiveChannel[]
  liveCategories: LiveCategory[]
  vodItems: VodItem[]
  vodCategories: LiveCategory[]
  series: Series[]
  seriesCategories: LiveCategory[]
}

/** Resultado REAL da gravação. `set()` devolvia `void` e engolia
 *  QuotaExceededError num catch vazio — foi assim que a tela de Configurações
 *  passou a exibir "15329 títulos atualizados" com o IndexedDB vazio (medido na
 *  LG do dono, 3 boots). Quem grava agora é obrigado a olhar o que voltou. */
export interface SnapshotWriteResult {
  /** true SÓ se todas as surfaces entraram. Nunca derive sucesso de "não
   *  lançou": a gravação falha sem exceção que suba pro chamador. */
  ok: boolean
  /** Surfaces efetivamente persistidas E validadas pelo meta. */
  persisted: SurfaceName[]
  /** Surfaces que ficaram de fora. */
  failed: SurfaceName[]
  /** Alguma falha foi por cota (≠ IDB indisponível). */
  quotaExceeded: boolean
  /** IDB inteiro fora (Chr38 sem Proxy, modo privado, open rejeitado).
   *  Não é bug — é o fallback de rede assumindo. Distinto de `quotaExceeded`
   *  de propósito: um é ambiente, o outro é orçamento. */
  unavailable: boolean
  /**
   * A gravação NÃO foi tentada, por decisão do disjuntor (catalog-write-breaker):
   *   - `breaker-off`: o aparelho não comporta nem a prioridade; nada se grava.
   *   - `unchanged`:   o disco já tem ESTE conteúdo (mesmo hash) nas surfaces
   *                    permitidas — regravar só gastaria capacidade.
   * Quem produz é catalog-snapshot.ts, não `set()`.
   */
  skipped?: 'breaker-off' | 'unchanged' | 'throttled'
  /**
   * A gravação foi tentada e estourou o prazo: o desfecho é DESCONHECIDO (pode
   * completar depois). Não é sucesso. Existe pra a tela nunca mais tratar
   * "sem resultado" como "gravou" — era a mentira que sobrava.
   * Quem produz é catalog-snapshot.ts, não `set()`.
   */
  timedOut?: true
}

/** Cota estourada, atravessando as encarnações do erro que o alvo (Chr38→Chr53+)
 *  produz. Detecta por CAPABILITY do erro, nunca por marca de device:
 *   - `QuotaExceededError` — nome padrão (DOMException moderno).
 *   - `code === 22` — QUOTA_EXCEEDED_ERR legado, o que o Chromium antigo entrega.
 *   - `NS_ERROR_DOM_QUOTA_REACHED` — Gecko (o app roda em browser também).
 *  Erro desconhecido NÃO vira cota: seria mascarar bug de verdade como "disco
 *  cheio" e mandar o app evictar dado à toa. */
function isQuotaError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false
  const e = err as { name?: unknown; code?: unknown }
  if (e.name === 'QuotaExceededError' || e.name === 'NS_ERROR_DOM_QUOTA_REACHED') return true
  return e.code === 22
}

interface Schema extends DBSchema {
  meta: { key: string; value: SnapshotMeta }
  liveChannels: { key: string; value: LiveChannel[] }
  liveCategories: { key: string; value: LiveCategory[] }
  vodItems: { key: string; value: VodItem[] }
  vodCategories: { key: string; value: LiveCategory[] }
  series: { key: string; value: Series[] }
  seriesCategories: { key: string; value: LiveCategory[] }
}

const DB_NAME = 'iptv-catalog'
/** O nome do banco, pra quem precisa apagá-lo pelo nome (a auto-cura da cota,
 *  catalog-auto-cura.ts). Uma fonte só: sem uma segunda cópia do literal. */
export const CATALOG_DB_NAME = DB_NAME
// v1 → v2: split do record único em stores por surface. Ver cabeçalho.
// v2 → v3: `meta` ganhou `surfaces` (quais surfaces entraram). Um meta v2 não
// tem a lista e seria lido como "nenhuma surface válida"; em vez de carregar
// esse ramo pra sempre, a base é recriada. Custo = 1 re-download de cache.
const DB_VERSION = 3

/**
 * Abertura via o molde unico (idb-opener.ts): solta a promise quando a
 * abertura falha, com guarda de identidade e janela de reabertura, e registra
 * o banco pro "Limpar dados". Antes, este modulo tinha o proprio `getDb`
 * com meia correcao (soltava a promise, mas sem guarda de identidade e sem
 * janela — uma rejeicao atrasada podia derrubar uma conexao nova, e uma
 * rajada de chamadas virava uma rajada de aberturas falhas).
 *
 * Falha de abertura continua fluindo pro catch de cada metodo (→ rede).
 */
const catalogDb = makeDbOpener<Schema>({
  name: DB_NAME,
  version: DB_VERSION,
  upgrade(db, oldVersion, _newVersion, tx) {
    // v1 → v2: derruba a store monolítica. O dado é cache — re-baixa.
    // `contains` guarda o 1º boot pós-install (IDB vazio), onde ela nunca
    // existiu: deleteObjectStore numa store ausente lançaria e abortaria
    // o upgrade inteiro, deixando o app sem IDB.
    if (db.objectStoreNames.contains('snapshots' as never)) {
      db.deleteObjectStore('snapshots' as never)
    }
    if (!db.objectStoreNames.contains('meta')) {
      db.createObjectStore('meta')
    }
    for (const surface of SURFACE_NAMES) {
      if (!db.objectStoreNames.contains(surface)) {
        db.createObjectStore(surface)
      }
    }
    // v2 → v3: os metas gravados pelo v2 não têm `surfaces`. Em vez de
    // carregar pra sempre um ramo "meta sem lista = tudo válido", zera o
    // conjunto: é cache, custa 1 fetch e nasce já no formato novo.
    // Só roda saindo de uma base v2 real (oldVersion 0 = install novo, 1 =
    // stores recém-criadas e vazias).
    if (oldVersion === 2) {
      for (const name of ['meta', ...SURFACE_NAMES] as const) {
        tx.objectStore(name).clear()
      }
    }
  },
})

function getDb(): Promise<IDBPDatabase<Schema>> {
  return catalogDb.get()
}

/** Default TTL: 24h. Provedores atualizam ~1×/dia. */
export const SNAPSHOT_DEFAULT_TTL_MS = 24 * 60 * 60 * 1000

/** Meta utilizável: presente, do schema que este código entende e com a lista de
 *  surfaces. `surfaces` ausente = meta gravado por uma versão anterior (DB v2),
 *  que só sabia dizer "tudo ou nada" — tratar como inválido força o re-download
 *  em vez de adivinhar o que está em disco. */
function isUsableMeta(meta: SnapshotMeta | undefined): meta is SnapshotMeta {
  if (!meta) return false
  if (meta.schemaVersion !== SNAPSHOT_SCHEMA_VERSION) return false
  return Array.isArray(meta.surfaces)
}

/** Recorta os hashes às surfaces que de fato entraram. */
function hashesPersistidos(
  hashes: Partial<Record<SurfaceName, string>> | undefined,
  persisted: readonly SurfaceName[],
): Partial<Record<SurfaceName, string>> | undefined {
  if (!hashes) return undefined
  const out: Partial<Record<SurfaceName, string>> = {}
  for (const s of persisted) {
    const h = hashes[s]
    if (h !== undefined) out[s] = h
  }
  return out
}

/** Quebra o snapshot nas surfaces persistidas. */
function toSurfaces(snapshot: CatalogSnapshot): SurfaceValueMap {
  return {
    liveChannels: snapshot.liveChannels,
    liveCategories: snapshot.liveCategories,
    vodItems: snapshot.vodItems,
    vodCategories: snapshot.vodCategories,
    series: snapshot.series,
    seriesCategories: snapshot.seriesCategories,
  }
}

export const catalogStore = {
  /**
   * Lê SÓ os metadados (barato — não toca no payload). Use quando a pergunta
   * for "está fresco?" / "qual o contentHash?" — que é o caso do boot.
   * Retorna null se ausente ou shape incompatível (força re-download).
   */
  async getMeta(providerId: string): Promise<SnapshotMeta | null> {
    try {
      const db = await getDb()
      const meta = await db.get('meta', providerId)
      if (!isUsableMeta(meta)) return null
      return meta
    } catch {
      return null
    }
  },

  /**
   * Lê UMA surface do snapshot local (ex: 'liveChannels'), sem desserializar
   * o resto do catálogo. Este é o caminho quente do local-first.
   *
   * Valida meta antes: sem meta (ou schema incompatível) = sem snapshot válido,
   * mesmo que a store da surface tenha resto de uma gravação parcial.
   * NÃO checa staleness — quem decide TTL é o chamador (ver api.ts).
   */
  async getSurface<K extends SurfaceName>(
    providerId: string,
    surface: K,
  ): Promise<SurfaceValueMap[K] | null> {
    try {
      const db = await getDb()
      const meta = await db.get('meta', providerId)
      if (!isUsableMeta(meta)) return null
      // A surface tem que constar do meta DESTE provider. Sem esta checagem,
      // resto de uma gravação anterior (ou de um provider trocado) seria
      // servido como se fosse do snapshot corrente.
      if (!meta.surfaces.includes(surface)) return null
      const value = await db.get(surface, providerId)
      if (!value) return null
      return value as SurfaceValueMap[K]
    } catch {
      return null
    }
  },

  /**
   * Lê o snapshot COMPLETO do cache local (todas as surfaces + meta).
   * Custa a desserialização inteira — só use quando precisar de tudo
   * (catalog-snapshot, search suggestions). Pra staleness use getMeta;
   * pra uma surface use getSurface.
   *
   * Retorna null se ausente ou shape incompatível.
   */
  async get(providerId: string): Promise<SnapshotRecord | null> {
    try {
      const db = await getDb()
      const meta = await db.get('meta', providerId)
      if (!isUsableMeta(meta)) return null
      // Exige o conjunto COMPLETO: este é o leitor de "me dá tudo". Numa
      // gravação parcial (ex: sem `vodItems`), devolver o record capenga faria
      // a busca e o catalog-snapshot acharem que o catálogo encolheu. Quem
      // aguenta parcial usa getSurface, que é o caminho quente.
      if (SURFACE_NAMES.some((s) => !meta.surfaces.includes(s))) return null
      // Uma transação só pras 6 surfaces — consistente e sem 6 round-trips.
      const tx = db.transaction(SURFACE_NAMES, 'readonly')
      const [liveChannels, liveCategories, vodItems, vodCategories, series, seriesCategories] =
        await Promise.all([
          tx.objectStore('liveChannels').get(providerId),
          tx.objectStore('liveCategories').get(providerId),
          tx.objectStore('vodItems').get(providerId),
          tx.objectStore('vodCategories').get(providerId),
          tx.objectStore('series').get(providerId),
          tx.objectStore('seriesCategories').get(providerId),
        ])
      await tx.done
      // Gravação parcial (quota estourou no meio) — trata como ausente em vez
      // de devolver um snapshot capenga pra tela.
      if (
        !liveChannels ||
        !liveCategories ||
        !vodItems ||
        !vodCategories ||
        !series ||
        !seriesCategories
      ) {
        return null
      }
      return {
        providerId: meta.providerId,
        contentHash: meta.contentHash,
        storedAt: meta.storedAt,
        bandwidthMbps: meta.bandwidthMbps,
        snapshot: {
          schemaVersion: meta.schemaVersion,
          generatedAt: meta.generatedAt,
          providerId: meta.providerId,
          liveCategories,
          liveChannels,
          vodCategories,
          vodItems,
          seriesCategories,
          series,
        },
      }
    } catch {
      return null
    }
  },

  /**
   * Grava snapshot. Sobrescreve qualquer registro anterior do mesmo providerId.
   * Bandwidth: passa null se não foi medido nesta operação (ex: cache hit
   * sem novo download).
   *
   * Escreve UMA TRANSAÇÃO POR SURFACE, na ordem de SURFACE_WRITE_PRIORITY, e o
   * `meta` por último (é ele que valida o conjunto). Se a cota acabar no meio,
   * as surfaces que já entraram continuam válidas e as outras degradam pra rede
   * — em vez de perder tudo, inclusive `liveChannels`, como acontecia com a
   * transação multi-store única.
   *
   * ⚠️ NUNCA derive sucesso de "não lançou": esta função não propaga exceção.
   * O resultado é o valor retornado. Foi ignorá-lo que fez Configurações exibir
   * "15329 títulos atualizados" com o IndexedDB vazio.
   */
  async set(
    providerId: string,
    contentHash: string,
    snapshot: CatalogSnapshot,
    bandwidthMbps: number | null,
    opts?: {
      surfaces?: readonly SurfaceName[]
      /** Hashes de conteúdo das surfaces (ver `SnapshotMeta.hashes`). Só os das
       *  que de fato entrarem vão pro meta. */
      hashes?: Partial<Record<SurfaceName, string>>
      /** Contexto da decisão, pra telemetria (`snapshot_write`). */
      telemetria?: { motivo: string; modo: string; gravacoesSessao: number }
    },
  ): Promise<SnapshotWriteResult> {
    const startedAt = Date.now()
    const itemsTotal =
      snapshot.liveChannels.length + snapshot.vodItems.length + snapshot.series.length

    const report = (r: SnapshotWriteResult): SnapshotWriteResult => {
      const result: 'ok' | 'partial' | 'failed' | 'unavailable' = r.unavailable
        ? 'unavailable'
        : r.ok
          ? 'ok'
          : r.persisted.length > 0
            ? 'partial'
            : 'failed'
      // Grita. `unavailable` sai como info (é o fallback de rede funcionando);
      // cota estourada sai como warn (é orçamento excedido, alguém tem que ver).
      if (!r.ok) {
        const line = `[catalog-store] gravação ${result}: ${r.persisted.length}/${SURFACE_NAMES.length} surfaces` +
          (r.failed.length > 0 ? ` — fora: ${r.failed.join(', ')}` : '') +
          (r.quotaExceeded ? ' — cota do IndexedDB estourada' : '')
        if (r.unavailable) console.info(line)
        else console.warn(line)
      }
      track({
        type: 'snapshot_write',
        ts: Math.floor(Date.now() / 1000),
        result,
        quotaExceeded: r.quotaExceeded,
        surfacesPersisted: r.persisted.length,
        surfacesTotal: SURFACE_NAMES.length,
        failedSurfaces: r.failed.join(','),
        itemsTotal,
        elapsedMs: Date.now() - startedAt,
        tier: getCapability('cpuTier'),
        // Por que gravou, em que modo do disjuntor, e quantas gravações REAIS
        // esta sessão já fez — a conta que mede a sangria no aparelho.
        ...(opts?.telemetria ?? {}),
      })
      return r
    }

    let db: IDBPDatabase<Schema>
    try {
      db = await getDb()
    } catch {
      // IDB inteiro fora (Chr38 sem Proxy, modo privado). O app degrada pro
      // caminho de rede — limpo e esperado. NÃO é cota.
      return report({
        ok: false,
        persisted: [],
        failed: [...SURFACE_NAMES],
        quotaExceeded: false,
        unavailable: true,
      })
    }

    // Invalida o meta ANTES de reescrever as surfaces: durante a gravação o
    // conjunto está inconsistente (surfaces novas + meta velho). Sem isso, um
    // leitor concorrente poderia casar meta antigo com surface nova.
    try {
      await db.delete('meta', providerId)
    } catch {
      // Meta preso é o pior caso: apontaria pra dado que estamos substituindo.
      // Aborta a gravação em vez de arriscar servir mistura.
      return report({
        ok: false,
        persisted: [],
        failed: [...SURFACE_NAMES],
        quotaExceeded: false,
        unavailable: false,
      })
    }

    // Apaga as surfaces antigas DESTE provider antes de escrever as novas.
    // Elas vão ser substituídas de qualquer forma, e removê-las primeiro devolve
    // a cota que ocupam — sem isso, versão velha e nova disputam o mesmo
    // orçamento e o snapshot novo estoura por causa do que ele mesmo vai trocar.
    for (const s of SURFACE_NAMES) {
      try {
        await db.delete(s, providerId)
      } catch {
        // Não conseguir limpar não impede tentar gravar: no pior caso a surface
        // nova não entra e isso aparece em `failed`.
      }
    }

    const surfaces = toSurfaces(snapshot)
    // _padding NÃO é persistido: ele só existe pra medir banda no download
    // (era descartado no v1 também). Como não há campo pra ele nas surfaces,
    // o split o descarta por construção.
    const persisted: SurfaceName[] = []
    const failed: SurfaceName[] = []
    let quotaExceeded = false

    // UMA TRANSAÇÃO POR SURFACE, na ordem de prioridade.
    //
    // O `set()` antigo abria UMA transação multi-store com as 6 surfaces: o IDB
    // precisa efetivar isso como unidade atômica, então os ~10MB+ da conta
    // medida eram cobrados de uma vez e a transação inteira abortava — inclusive
    // os 391KB de `liveChannels`, que cabem com folga. Medido no device (mesmo
    // padrão de transação): 5MB grava, 10MB estoura. Fatiando, cada transação
    // é cobrada sozinha e `liveChannels` passa a ser um write de ~391KB, ~13x
    // abaixo do teto já provado — enquanto `vodItems`, o gigante, falha sozinho
    // sem levar o resto junto.
    //
    // `opts.surfaces` restringe O QUE se tenta gravar (o disjuntor em
    // catalog-write-breaker.ts passa só a prioridade quando o aparelho já mostrou
    // pressão). O que fica de fora por essa política entra em `failed` — não foi
    // persistido, e dizer o contrário seria a mentira do "15329 atualizados".
    const permitidas = opts?.surfaces ?? SURFACE_WRITE_PRIORITY
    let pararPorCota = false
    for (const s of SURFACE_WRITE_PRIORITY) {
      if (!permitidas.includes(s) || pararPorCota) {
        failed.push(s)
        continue
      }
      try {
        const tx = db.transaction(s, 'readwrite')
        await Promise.all([tx.objectStore(s).put(surfaces[s] as never, providerId), tx.done])
        persisted.push(s)
      } catch (err) {
        failed.push(s)
        if (isQuotaError(err)) {
          quotaExceeded = true
          // PARA na primeira recusa por cota. Antes o laço seguia tentando as
          // próximas ("podem ser menores"). A medição no aparelho inverteu essa
          // aposta: escrever e apagar NÃO devolve o espaço na hora, e um único
          // teste derrubou a capacidade de 1 MB pra 100 KB. Cada tentativa que
          // falha ainda custa. Se esta surface não coube, as seguintes — de
          // prioridade MENOR, e `series`/`vodItems` MUITO maiores — só gastariam
          // a capacidade que favoritos e progresso precisam, pra falhar igual.
          pararPorCota = true
        }
        // Erro que NÃO é cota (transitório, bug) não para o laço: nada indica
        // que as próximas vão falhar, e parar esconderia o que cabia.
      }
    }

    if (persisted.length === 0) {
      return report({ ok: false, persisted: [], failed, quotaExceeded, unavailable: false })
    }

    // Meta POR ÚLTIMO: é ele que valida o conjunto. Enquanto não entra, tudo
    // acima é lido como "sem snapshot" — nunca como catálogo pela metade.
    const hashesGravados = hashesPersistidos(opts?.hashes, persisted)
    try {
      await db.put(
        'meta',
        {
          providerId,
          contentHash,
          storedAt: Math.floor(Date.now() / 1000),
          bandwidthMbps,
          schemaVersion: snapshot.schemaVersion,
          generatedAt: snapshot.generatedAt,
          surfaces: persisted,
          // Só os hashes do que ENTROU: hash de surface que não foi gravada
          // afirmaria em disco um conteúdo que não está lá.
          // (`exactOptionalPropertyTypes`: spread condicional, nunca `hashes: undefined`.)
          ...(hashesGravados ? { hashes: hashesGravados } : {}),
          writtenAt: Math.floor(Date.now() / 1000),
        },
        providerId,
      )
    } catch (err) {
      // Sem meta nada é legível, então o que entrou não conta como persistido.
      return report({
        ok: false,
        persisted: [],
        failed: [...SURFACE_NAMES],
        quotaExceeded: quotaExceeded || isQuotaError(err),
        unavailable: false,
      })
    }

    return report({
      ok: failed.length === 0,
      persisted,
      failed,
      quotaExceeded,
      unavailable: false,
    })
  },

  /**
   * Libera cota sacrificando as surfaces pesadas e PRESERVANDO `liveChannels`
   * (+ categorias). Chamada sob pressão de cota, no lugar de apagar o snapshot
   * inteiro.
   *
   * Por que existe: o idb-quota-manager fazia `clear(providerId)` sob pressão,
   * que derruba TUDO — inclusive os ~391KB de `liveChannels`, o caminho crítico
   * do 1º frame do LiveHero. Sacrificava o vídeo (o produto) pra liberar espaço
   * que o VOD (~o grosso dos 15.329 títulos) estava ocupando. Isto inverte:
   * cai `vodItems`/`series`, que degradam pra rede sem quebrar nada.
   *
   * @returns as surfaces efetivamente evictadas.
   */
  async evictSacrificial(providerId: string): Promise<SurfaceName[]> {
    const evicted: SurfaceName[] = []
    try {
      const db = await getDb()
      const meta = await db.get('meta', providerId)
      if (!isUsableMeta(meta)) return []
      for (const s of SURFACE_SACRIFICIAL) {
        if (!meta.surfaces.includes(s)) continue
        await db.delete(s, providerId)
        evicted.push(s)
      }
      if (evicted.length === 0) return []
      // Meta reescrito pra refletir a verdade: quem ler `vodItems` agora recebe
      // null e vai pra rede, em vez de achar que o dado sumiu sem explicação.
      await db.put(
        'meta',
        { ...meta, surfaces: meta.surfaces.filter((s) => !evicted.includes(s)) },
        providerId,
      )
      return evicted
    } catch {
      return evicted
    }
  },

  /**
   * Renova o `storedAt` (reset do TTL) SEM reescrever o payload. Usado no 304:
   * o backend confirmou que o snapshot local ainda vale.
   *
   * No DB v1 isso exigia regravar o record inteiro (5-15MB de escrita em IDB
   * por um timestamp) — churn caro num device com quota apertada. Com o meta
   * separado, é 1 put minúsculo.
   *
   * @returns true se renovou; false se não havia meta (nada a renovar) ou o
   * IDB falhou — o chamador decide (tipicamente: re-baixar).
   */
  async touch(providerId: string): Promise<boolean> {
    try {
      const db = await getDb()
      const meta = await db.get('meta', providerId)
      if (!meta) return false
      await db.put('meta', { ...meta, storedAt: Math.floor(Date.now() / 1000) }, providerId)
      return true
    } catch {
      return false
    }
  },

  /**
   * Detecta se o snapshot está stale (passou do TTL).
   * Aceita meta OU record — ambos carregam `storedAt` (unix sec).
   * @param ttlMs default 24h
   */
  isStale(record: { storedAt: number }, ttlMs = SNAPSHOT_DEFAULT_TTL_MS): boolean {
    const ageMs = Date.now() - record.storedAt * 1000
    return ageMs > ttlMs
  },

  async clear(providerId?: string): Promise<void> {
    try {
      const db = await getDb()
      const stores = ['meta', ...SURFACE_NAMES] as const
      const tx = db.transaction(stores, 'readwrite')
      await Promise.all([
        ...stores.map((s) =>
          providerId ? tx.objectStore(s).delete(providerId) : tx.objectStore(s).clear(),
        ),
        tx.done,
      ])
    } catch {
      // ignora
    }
  },
}

// Tipos exportados pra outros módulos lerem (opcional)
export type { SnapshotRecord }
// Re-export tipos do iptv-client pra conveniência
export type { LiveCategory, LiveChannel, VodItem, Series, EpgProgram }
